import { useState, useRef, useEffect, useCallback, createContext, type ComponentProps, type ReactNode } from 'react'
import { Popover, useButtonGroupContext } from '@highlight/ui'
import { twMerge } from 'tailwind-merge'
import { FeedbackTrigger } from './nested/trigger'
import { FeedbackInput } from './nested/input'
import { useMessageFeedback, type MessageFeedbackState } from './use-message-feedback'
import { feedbackSubjectKey, type FeedbackSubject } from '@renderer/chat/api/feedback'
import { getArchiveAfterFeedbackSuccess, type ArchiveAfterFeedback } from './archive-after-feedback'
import { logAmplitudeEvent } from '@renderer/chat/utils/amplitude'
import type { FeedbackSource } from '@renderer/chat/types/events/chat'

export type { FeedbackSource }

interface FeedbackContextValue {
  open: boolean
  setOpen: (open: boolean) => void
  type: 'positive' | 'negative'
  setType: (type: 'positive' | 'negative') => void
  text: string
  setText: (text: string) => void
  handleSend: () => void
  handleOpenChange: (isOpen: boolean) => void
  isPending: boolean
  confirmed: boolean
  isSelected: boolean
  canArchive: boolean
  archiveAfterSubmit: boolean
  setArchiveAfterSubmit: (archive: boolean) => void
}

export const FeedbackContext = createContext<FeedbackContextValue>({} as FeedbackContextValue)

/** Rating is host-driven when controlled, so the popup's own setter is inert. */
const noopSetType = () => {}

interface FeedbackProps {
  onSubmit: (type: 'positive' | 'negative', message: string, archiveAfterSubmit: boolean) => void | Promise<void>
  source: FeedbackSource
  isPending?: boolean
  isSelected?: boolean
  /** Controlled open state, for hosts that open this from their own affordance (e.g. a context menu). */
  open?: boolean
  /** Controlled rating, set alongside `open` when the host's affordance picks it. */
  type?: 'positive' | 'negative'
  onOpenChange?: (open: boolean) => void
  canArchive?: boolean
  children: ReactNode
}

function Feedback({
  onSubmit,
  source,
  isPending = false,
  isSelected = false,
  open: controlledOpen,
  type: controlledType,
  onOpenChange,
  canArchive = false,
  children,
}: FeedbackProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
  const [uncontrolledType, setUncontrolledType] = useState<'positive' | 'negative'>('positive')
  const [text, setText] = useState('')
  const [confirmed, setConfirmed] = useState(false)
  const [archiveAfterSubmit, setArchiveAfterSubmit] = useState(true)
  const sentRef = useRef(false)

  const open = controlledOpen ?? uncontrolledOpen
  const type = controlledType ?? uncontrolledType

  // Every path that closes this — including the post-send auto-close below —
  // goes through here, so hosts mirroring the state can't miss one.
  const onOpenChangeRef = useRef(onOpenChange)
  onOpenChangeRef.current = onOpenChange
  const setOpen = useCallback((next: boolean) => {
    setUncontrolledOpen(next)
    onOpenChangeRef.current?.(next)
  }, [])
  const setType = controlledType === undefined ? setUncontrolledType : noopSetType

  useEffect(() => {
    if (isPending || !sentRef.current) return
    sentRef.current = false
    setConfirmed(true)
    const closeTimer = setTimeout(() => {
      setOpen(false)
      setText('')
      setArchiveAfterSubmit(true)
      setTimeout(() => setConfirmed(false), 300)
    }, 1500)
    return () => clearTimeout(closeTimer)
  }, [isPending])

  const handleSend = () => {
    if (isPending || confirmed) return
    const trimmed = text.trim()
    onSubmit(type, trimmed, type === 'negative' && archiveAfterSubmit)
    sentRef.current = true
    logAmplitudeEvent('Feedback Submitted', {
      source,
      rating: type,
      has_message: trimmed.length > 0,
      message: trimmed || undefined,
    })
  }

  const handleOpenChange = (isOpen: boolean) => {
    if (isPending) return
    setOpen(isOpen)
    if (!isOpen) {
      setText('')
      setArchiveAfterSubmit(true)
      setConfirmed(false)
      sentRef.current = false
    }
  }

  return (
    <FeedbackContext.Provider
      value={{
        open,
        setOpen,
        type,
        setType,
        text,
        setText,
        handleSend,
        handleOpenChange,
        isPending,
        confirmed,
        isSelected,
        canArchive,
        archiveAfterSubmit,
        setArchiveAfterSubmit,
      }}
    >
      <Popover open={open} onOpenChange={handleOpenChange}>
        {children}
      </Popover>
    </FeedbackContext.Provider>
  )
}

interface MessageFeedbackProps {
  subject: FeedbackSubject
  source: FeedbackSource
  className?: string
  initialRating?: 'positive' | 'negative' | null
  /** True while either comment popover is open, so hosts can hold themselves open under it. */
  onOpenChange?: (open: boolean) => void
  /** Offered on negative feedback; runs only after the feedback request succeeds. */
  onArchive?: ArchiveAfterFeedback
}

function MessageFeedback(props: MessageFeedbackProps) {
  return <MessageFeedbackSession key={feedbackSubjectKey(props.subject)} {...props} />
}

function MessageFeedbackSession({
  subject,
  source,
  className,
  initialRating,
  onOpenChange,
  onArchive,
}: MessageFeedbackProps) {
  const inGroup = useButtonGroupContext() !== null
  const { mutate, isPending, rating } = useMessageFeedback(subject, initialRating ?? null)
  const submit = (type: 'positive' | 'negative', message: string, archiveAfterSubmit: boolean) =>
    mutate({ type, message }, { onSuccess: getArchiveAfterFeedbackSuccess(archiveAfterSubmit, onArchive) })
  const onOpenChangeRef = useRef(onOpenChange)
  onOpenChangeRef.current = onOpenChange

  const [positiveOpen, setPositiveOpen] = useState(false)
  const [negativeOpen, setNegativeOpen] = useState(false)
  const anyOpen = positiveOpen || negativeOpen
  useEffect(() => {
    onOpenChangeRef.current?.(anyOpen)
  }, [anyOpen])

  const pair = (
    <>
      <Feedback
        onSubmit={submit}
        source={source}
        isPending={isPending}
        isSelected={rating === 'positive'}
        onOpenChange={setPositiveOpen}
        canArchive={false}
      >
        <FeedbackTrigger type="positive" />
        <FeedbackInput />
      </Feedback>
      <Feedback
        onSubmit={submit}
        source={source}
        isPending={isPending}
        isSelected={rating === 'negative'}
        onOpenChange={setNegativeOpen}
        canArchive={!!onArchive}
      >
        <FeedbackTrigger type="negative" />
        <FeedbackInput />
      </Feedback>
    </>
  )

  // In a ButtonGroup the thumbs are the group's own children, so its spacing and frame apply.
  // Alone they need a row of their own.
  if (inGroup) return pair
  return <div className={twMerge('flex items-center gap-0.5', className)}>{pair}</div>
}

interface FeedbackPopoverProps {
  /** The host owns the hook when it also paints the rating somewhere (e.g. a menu icon). */
  feedback: MessageFeedbackState
  source: FeedbackSource
  /** The rating the host's affordance picked; `null` closes the popup. */
  type: 'positive' | 'negative' | null
  onClose: () => void
  /** What to hang the popup on, since the host owns the affordance that opened it. */
  anchor: NonNullable<ComponentProps<typeof FeedbackInput>>['anchor']
  /** Offered on negative feedback; runs only after the feedback request succeeds. */
  onArchive?: ArchiveAfterFeedback
}

/**
 * The rating comment box alone, opened by a host's own affordance — a context
 * menu item, say — instead of by a thumb trigger.
 *
 * A peer of `MessageFeedback` rather than a mode of it: both post the same
 * rating the same way, and differ only in what picks it. Hosts that render the
 * pair of thumbs want `MessageFeedback`; hosts that already have somewhere to
 * pick a rating want this, and get the comment box, send, confirmation and
 * Enter/Escape handling without restating any of it.
 */
function FeedbackPopover({ feedback, source, type, onClose, anchor, onArchive }: FeedbackPopoverProps) {
  const { mutate, isPending } = feedback

  return (
    <Feedback
      open={type !== null}
      type={type ?? 'positive'}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose()
      }}
      onSubmit={(rating, message, archiveAfterSubmit) =>
        mutate({ type: rating, message }, { onSuccess: getArchiveAfterFeedbackSuccess(archiveAfterSubmit, onArchive) })
      }
      source={source}
      isPending={isPending}
      canArchive={!!onArchive}
    >
      <FeedbackInput anchor={anchor} />
    </Feedback>
  )
}

interface MessageFeedbackPopoverProps extends Omit<FeedbackPopoverProps, 'feedback'> {
  subject: FeedbackSubject
  initialRating?: 'positive' | 'negative' | null
}

function MessageFeedbackPopover(props: MessageFeedbackPopoverProps) {
  return <MessageFeedbackPopoverSession key={feedbackSubjectKey(props.subject)} {...props} />
}

function MessageFeedbackPopoverSession({ subject, initialRating, ...props }: MessageFeedbackPopoverProps) {
  return <FeedbackPopover feedback={useMessageFeedback(subject, initialRating ?? null)} {...props} />
}

export { Feedback, FeedbackPopover, MessageFeedback, MessageFeedbackPopover }
